/* KFH cart drawer. Every change goes through Shopify's AJAX cart API with `sections`, and the
   drawer swaps in the server-rendered parts, so prices and discounts always come from the cart. */
(() => {
  if (window.KFHCart) return;
  window.KFHCart = true;

  // Looked up each time: the theme editor (and theme dev's hot reload) replace the section's DOM.
  // On the cart page the inline cart (cart.kfh) wins over the header drawer.
  const getRoot = () => document.querySelector('[data-kfh-cart-inline]') || document.querySelector('[data-kfh-cart]');
  const isInline = () => getRoot().hasAttribute('data-kfh-cart-inline');
  if (!getRoot()) return;
  const cfg = window.KFH_CONFIG || { routes: {} };
  const sectionId = () => getRoot().dataset.sectionId;
  const desktop = window.matchMedia('(min-width: 990px)');
  let lastFocus = null;
  let ownRequestAt = 0;

  /* ---------- Rendering ---------- */
  const swapParts = (html) => {
    const root = getRoot();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    root.querySelectorAll('[data-kfh-cart-part]').forEach((part) => {
      const fresh = doc.querySelector(`[data-kfh-cart-part="${part.dataset.kfhCartPart}"]`);
      if (fresh) part.replaceWith(fresh);
    });
    const foot = doc.querySelector('[data-kfh-cart-foot]');
    root.querySelector('[data-kfh-cart-foot]').hidden = foot ? foot.hidden : false;
    document.dispatchEvent(new CustomEvent('cart:refresh')); // Route (package protection) re-prices on this event
    applyDismissed();
    applyRoute();
  };

  // Route adds its fee at checkout, not to the cart: while its toggle is on, show the fee in the total
  // so the drawer matches what checkout will charge.
  const applyRoute = () => {
    const root = getRoot();
    const est = root && root.querySelector('[data-kfh-total]');
    if (!est) return;
    const w = root.querySelector('route-protect-widget');
    const on = w && w.getAttribute('status') === 'true' && w.getAttribute('payer') !== 'merchant';
    const fee = on ? Math.round(parseFloat((w.getAttribute('quote') || '').replace(/[^0-9.]/g, '')) * 100) || 0 : 0;
    const total = window.KFH.money(Number(est.dataset.kfhTotal) + fee);
    est.querySelector('b').textContent = total;
  };

  const applyDismissed = () => {
    getRoot().querySelectorAll('[data-kfh-nudge]').forEach((n) => {
      try { if (sessionStorage.getItem(`kfh-nudge-${n.dataset.kfhNudge}`)) n.hidden = true; } catch (e) { /* storage blocked */ }
    });
  };

  const emitCount = async (cart) => {
    if (!cart) {
      try { cart = await (await fetch(`${cfg.routes.cart}.js`, { headers: { Accept: 'application/json' } })).json(); } catch (e) { return; }
    }
    document.dispatchEvent(new CustomEvent('kfh:cart:changed', { detail: cart }));
  };

  const refresh = async () => {
    const id = sectionId();
    const res = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(id)}`);
    const data = await res.json();
    if (data[id]) swapParts(data[id]);
  };

  // POST to a cart endpoint, asking Shopify to render this section in the same response.
  const post = async (url, body) => {
    ownRequestAt = Date.now();
    const id = sectionId();
    getRoot().classList.add('is-busy');
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: [id], sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || (cfg.strings && cfg.strings.error));
      if (data.sections && data.sections[id]) swapParts(data.sections[id]);
      else await refresh();
      return data;
    } finally {
      getRoot().classList.remove('is-busy');
    }
  };

  const flash = (key) => {
    const line = key && getRoot().querySelector(`[data-kfh-line="${CSS.escape(key)}"]`);
    if (!line) return;
    line.classList.add('is-added');
    line.scrollIntoView({ block: 'nearest' });
    setTimeout(() => line.classList.remove('is-added'), 2000);
  };

  /* ---------- Open / close ---------- */
  const open = () => {
    const root = getRoot();
    if (isInline()) { root.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    if (!root.hidden) return;
    lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.classList.add('kfh-cart-open'); // corner widgets step aside (kfh-base.css)
    requestAnimationFrame(() => root.classList.add('is-open'));
    const close = root.querySelector('.kfh-cart__head [data-kfh-cart-close]');
    if (close) close.focus({ preventScroll: true });
  };
  const close = () => {
    const root = getRoot();
    if (root.hidden || isInline()) return;
    root.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    document.documentElement.classList.remove('kfh-cart-open');
    setTimeout(() => { root.hidden = true; }, 320);
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  };

  // Refresh an open drawer: dim it meanwhile, and put focus back on Close if the swap replaced the focused element.
  const refreshOpen = async () => {
    const root = getRoot();
    root.classList.add('is-busy');
    try { await refresh(); } finally { root.classList.remove('is-busy'); }
    if (root.classList.contains('is-open') && !isInline() && !root.contains(document.activeElement)) {
      const btn = root.querySelector('.kfh-cart__head [data-kfh-cart-close]');
      if (btn) btn.focus({ preventScroll: true });
    }
  };

  // Public API used by kfh-base.js (quick add) and the PDP.
  window.KFH = window.KFH || {};
  window.KFH.cartDrawer = {
    // Open first, then refresh: the section render can take seconds, and the shopper needs feedback now.
    open: async () => { open(); await refreshOpen(); },
    // After an add that already happened elsewhere (add.js response passed in).
    openAfterAdd: async (added) => {
      open();
      await refreshOpen();
      const items = added && (added.items || [added]);
      if (items && items[0]) flash(items[0].key);
      emitCount();
    },
    refresh,
    close,
  };

  /* ---------- Events inside the drawer ---------- */
  document.addEventListener('click', async (e) => {
    const root = getRoot();
    const t = e.target.closest('button, [data-kfh-cart-close]');
    if (!t || !root || !root.contains(t)) return;

    if (t.matches('[data-kfh-cart-close]')) { close(); return; }

    const line = t.closest('[data-kfh-line]');
    if (t.matches('[data-kfh-line-qty]') && line) {
      const cart = await post(`${cfg.routes.cartChange}.js`, { id: line.dataset.kfhLine, quantity: Number(t.dataset.kfhLineQty) }).catch(() => null);
      emitCount(cart);
      return;
    }

    if (t.matches('[data-kfh-cart-add]') || t.matches('[data-kfh-cart-add-select]')) {
      const id = t.matches('[data-kfh-cart-add]')
        ? t.dataset.kfhCartAdd
        : t.closest('.kfh-closure').querySelector('[data-kfh-closure-select]').value;
      const data = await post(`${cfg.routes.cartAdd}.js`, { items: [{ id: Number(id), quantity: 1 }] }).catch(() => null);
      if (data && data.items) flash(data.items[0].key);
      emitCount();
      return;
    }

    if (t.matches('[data-kfh-nudge-dismiss]')) {
      try { sessionStorage.setItem(`kfh-nudge-${t.dataset.kfhNudgeDismiss}`, '1'); } catch (err) { /* storage blocked */ }
      applyDismissed();
      return;
    }

    if (t.matches('[data-kfh-code-remove]')) {
      // Re-send the remaining codes without this one.
      const keep = [...root.querySelectorAll('[data-kfh-code-remove]')].map((b) => b.dataset.kfhCodeRemove).filter((c) => c !== t.dataset.kfhCodeRemove);
      await post(`${cfg.routes.cartUpdate}.js`, { discount: keep.join(',') }).catch(() => null);
    }
  });

  // Changing a line's length: Shopify can't swap a line's variant, so remove it and add the new one.
  document.addEventListener('change', async (e) => {
    const select = e.target.closest('[data-kfh-cart] [data-kfh-line-variant]');
    if (!select) return;
    const line = select.closest('[data-kfh-line]');
    const qty = Number(line.querySelector('.kfh-qty span').textContent) || 1;
    try {
      await post(`${cfg.routes.cartChange}.js`, { id: line.dataset.kfhLine, quantity: 0 });
      const data = await post(`${cfg.routes.cartAdd}.js`, { items: [{ id: Number(select.value), quantity: qty }] });
      if (data && data.items) flash(data.items[0].key);
    } catch (err) {
      await refresh();
    }
    emitCount();
  });

  document.addEventListener('submit', async (e) => {
    const root = getRoot();
    const form = e.target.closest('[data-kfh-cart] [data-kfh-code-form]');
    if (!form) return;
    e.preventDefault();
    const code = form.querySelector('input').value.trim();
    if (!code) return;
    const existing = [...root.querySelectorAll('[data-kfh-code-remove]')].map((b) => b.dataset.kfhCodeRemove);
    const cart = await post(`${cfg.routes.cartUpdate}.js`, { discount: [...existing, code].join(',') }).catch(() => null);
    const applied = cart && (cart.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
    const msg = root.querySelector('[data-kfh-code-msg]');
    if (msg) msg.hidden = !!(applied && applied.applicable);
  });

  /* ---------- Global triggers ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-kfh-cart-open]');
    if (!opener) return;
    e.preventDefault();
    window.KFH.cartDrawer.open();
  });

  document.addEventListener('keydown', (e) => {
    const root = getRoot();
    if (!root || root.hidden || isInline()) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    const focusables = [...root.querySelectorAll('a[href], button:not([disabled]), select, input, summary')].filter((n) => n.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  // Route adds its protection item by itself; only a shopper's own add should open the drawer.
  const routeHandle = () => (getRoot().dataset.routeHandle || 'routeins');
  let lastShopperCount = null;
  const addedByShopper = async () => {
    try {
      const cart = await (await fetch(`${cfg.routes.cart}.js`, { headers: { Accept: 'application/json' } })).json();
      const n = cart.items.filter((i) => i.handle !== routeHandle()).reduce((a, i) => a + i.quantity, 0);
      const grew = lastShopperCount === null || n > lastShopperCount;
      lastShopperCount = n;
      return grew;
    } catch (e) { return true; }
  };
  addedByShopper();
  // Re-apply when Route draws its widget or the shopper flips the toggle (only Route's box is watched;
  // applyRoute never writes inside it, so no loop).
  const routeBox = getRoot() && getRoot().querySelector('.route-div');
  if (routeBox) new MutationObserver(applyRoute).observe(routeBox, { subtree: true, childList: true, attributes: true, attributeFilter: ['status', 'quote'] });

  // Adds made by other code (Ella's product forms use jQuery or fetch): watch the network
  // for /cart/add and open the drawer. Our own requests are skipped.
  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (!/\/cart\/add(\.js)?(\?|$)/.test(new URL(entry.name, location.href).pathname + '?')) return;
          if (!['fetch', 'xmlhttprequest'].includes(entry.initiatorType)) return;
          if (Date.now() - ownRequestAt < 1500 || Date.now() - (window.KFH.lastOwnAdd || 0) < 1500) return;
          addedByShopper().then((yes) => (yes ? window.KFH.cartDrawer.openAfterAdd() : refresh()));
        });
      }).observe({ type: 'resource', buffered: false });
    } catch (e) { /* older browsers: Ella's own add-to-cart message still shows */ }
  }

  // Theme editor: keep the drawer usable when its section is selected.
  document.addEventListener('shopify:section:select', (e) => { if (getRoot().closest(`#shopify-section-${e.detail.sectionId}`)) open(); });
  document.addEventListener('shopify:section:deselect', (e) => { if (getRoot().closest(`#shopify-section-${e.detail.sectionId}`)) close(); });

  applyDismissed();
  desktop.addEventListener('change', () => { const root = getRoot(); if (!root.hidden) root.classList.add('is-open'); });
})();
